<template>
  <v-dialog
    v-model="isVisible"
    persistent
    scrollable
    max-width="900"
  >
    <v-card>
      <v-card-title
        class="grey lighten-2"
      >
        <v-icon
          left
        >
          checklist
        </v-icon>
        Select a Checklist from the Library
      </v-card-title>
      <v-card-text
        class="px-0"
        style="height: 50vh;"
      >
        <v-container
          class="dialog-content"
          fluid
        >
          <v-row
            v-if="!checklists.length"
          >
            <v-col
              cols="12"
              class="align-center d-flex justify-center"
            >
              <div
                class="text-center"
              >
                <v-icon
                  size="128"
                  color="primary"
                >
                  mdi-alert-outline
                </v-icon>
                <div
                  class="grey--text mt-4 text-overline"
                >
                  There are no pre-configured Checklists on the Library.
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row
            v-if="checklists.length"
            key="selection-screen"
            class="h-100 my-0"
          >
            <v-col
              cols="12"
              md="4"
            >
              <v-list>
                <v-subheader>
                  Available Checklists
                </v-subheader>

                <v-text-field
                  v-model="searchTerm"
                  class="mx-4"
                  dense
                  label="Filter"
                  outlined
                />

                <v-list-item-group
                  v-if="filteredChecklists.length"
                  v-model="selectedChecklistId"
                >
                  <v-list-item
                    v-for="item of filteredChecklists"
                    :key="item.id"
                    :value="item.id"
                  >
                    <template
                      #default
                    >
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.title }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-list-item>
                </v-list-item-group>

                <v-list-item v-else>
                  There are no Checklists that match the filter
                </v-list-item>
              </v-list>
            </v-col>

            <v-divider vertical />

            <v-col
              cols="12"
              md="8"
            >
              <div style="position: sticky; top: 0px;">
                <v-list>
                  <v-subheader>
                    Checklist Items Preview
                  </v-subheader>

                  <v-list-item
                    v-for="item of selectedChecklistItems"
                    :key="item.id"
                  >
                    <template
                      #default
                    >
                      <v-list-item-action>
                        <v-checkbox
                          v-model="item.done"
                          disabled
                        />
                      </v-list-item-action>

                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          color="secondary"
          @click="onCancelAction"
        >
          Cancel
        </v-btn>

        <v-spacer />

        <v-btn
          color="primary"
          :disabled="!selectedChecklistId"
          @click="onAddChecklist"
        >
          Add Checklist
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'

import { sortByKeyCaseInsensitive } from '@/utilities'

import { CHECKLIST_NAMESPACE } from '@/store/checklist'

export default {
  name: 'ChecklistImportDialog',

  model: {
    prop: 'isEnabled',
    event: 'change',
  },

  props: {
    isEnabled: {
      type: Boolean,
      required: true,
    },
  },

  data () {
    return {
      checklists: [],
      searchTerm: '',
      selectedChecklistId: null,
    }
  },

  computed: {
    filteredChecklists () {
      return this.checklists.
        filter(checklist => !this.searchTerm || checklist.title.toLowerCase().includes(this.searchTerm))
    },

    isVisible () {
      return this.isEnabled
    },

    selectedChecklist () {
      if (!this.selectedChecklistId || !this.checklists) return null

      return this.checklists.find(r => r.id === this.selectedChecklistId) || null
    },

    selectedChecklistItems () {
      return this.selectedChecklist?.items || []
    },
  },

  watch: {
    isEnabled (newVal, oldVal) {
      if (oldVal !== (newVal === true)) this.selectedChecklistId = null
    },
  },

  mounted () {
    this.getChecklists().
      then(response => this.loadChecklists(response))
  },

  methods: {
    loadChecklists (data) {
      this.checklists = []
      const checklists = []

      data.forEach(cl => {
        // remove empty items and add an "id" to be used as key by vue
        const items = cl.items.
          filter(e => e.name.trim().length).
          map((e, i) => {
            return {
              done: e.done,
              id: i,
              name: e.name,
            }
          })

        checklists.push({
          id: cl.id,
          items: items,
          title: cl.title,
        })
      })

      this.checklists = sortByKeyCaseInsensitive(checklists, 'title')
    },

    onAddChecklist () {
      this.$emit('checklist-selected', {
        items: this.selectedChecklist.items.map(i => ({
          done: i.done,
          name: i.name,
        })),
        title: this.selectedChecklist.title,
      })
    },

    onCancelAction () {
      this.$emit('change', false)
    },

    ...mapActions(CHECKLIST_NAMESPACE, [
      'getChecklists',
    ]),
  },
}
</script>
